import React from 'react';
import { Download, Terminal } from 'lucide-react';
import { Section, SectionHeader } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Reveal } from '../components/ui/Reveal';

export default function About() {
  const skills = [
    { name: 'React', tone: 'accent' },
    { name: 'Python', tone: 'success' },
    { name: 'Node.js', tone: 'success' },
    { name: 'TypeScript', tone: 'accent' },
    { name: 'MongoDB', tone: 'success' },
    { name: 'Next.js', tone: 'info' },
    { name: 'TensorFlow', tone: 'warning' },
    { name: 'Docker', tone: 'info' },
    { name: 'AWS', tone: 'warning' },
    { name: 'PostgreSQL', tone: 'info' },
  ];

  const facts = [
    { label: 'Years coding', value: '4+' },
    { label: 'Current role', value: 'Senior SWE / Foxwel.ai' },
    { label: 'Focus', value: 'Full stack & AI systems' },
  ];

  return (
    <Section id="about">
      <SectionHeader
        title="About Me"
        subtitle="Computer Science AIML graduate building robust software applications."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-start">
        <Reveal className="space-y-5">
          <p className="text-base text-[var(--fg-secondary)] leading-relaxed">
            I am a 2025 B.E. Computer Science graduate specializing in{' '}
            <strong className="text-[var(--fg-primary)] font-medium">
              Artificial Intelligence & Machine Learning
            </strong>{' '}
            and{' '}
            <strong className="text-[var(--fg-primary)] font-medium">Full Stack Development</strong>.
          </p>

          <p className="text-base text-[var(--fg-secondary)] leading-relaxed">
            I craft scalable web applications and AI-driven solutions using React, Node.js, Python,
            and the MERN stack. My experience spans full-stack e-commerce systems, real-time
            analytics platforms, and intelligent agent workflows.
          </p>

          <div>
            <h3 className="t-label text-[var(--fg-primary)] mb-3">Core Technologies</h3>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <Badge key={skill.name} className="py-1 px-3">
                  <span>{skill.name}</span>
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-1">
            <Button asChild variant="primary" size="md">
              <a href="#projects">View Projects</a>
            </Button>

            <Button asChild variant="secondary" size="md">
              <a href="/resume/Rakshan_Shetty_Resume.pdf" download>
                <Download size={14} className="mr-1.5" />
                Download Resume
              </a>
            </Button>
          </div>
        </Reveal>

        <Reveal delay={0.08} className="space-y-4">
          <Card className="p-5 sm:p-6">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-[var(--bg-recessed)]">
              <div className="flex items-center">
                <span className="text-sm font-medium text-[var(--fg-primary)]">
                  Available for roles
                </span>
              </div>
              <Terminal size={14} className="text-[var(--fg-muted)]" aria-hidden="true" />
            </div>

            <div className="font-mono">
              <p className="text-xs text-[var(--fg-muted)] mb-2">developer.json</p>
              <pre className="text-sm text-[var(--fg-secondary)] overflow-x-auto leading-relaxed">
{`{
  "name": "Rakshan Shetty",
  "degree": "B.E. Computer Science (AIML)",
  "year": 2025,
  "location": "Bantwal, India",
  "status": "Available for roles"
}`}
              </pre>
            </div>

            <dl className="mt-5 pt-4 border-t border-[var(--bg-recessed)] space-y-3">
              {facts.map((fact) => (
                <div
                  key={fact.label}
                  className="flex items-baseline justify-between gap-4"
                >
                  <dt className="text-xs text-[var(--fg-muted)]">{fact.label}</dt>
                  <dd className="text-sm font-medium text-[var(--fg-primary)] text-right">
                    {fact.value}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>
        </Reveal>
      </div>
    </Section>
  );
}
